import type { FichaDoPaciente } from '@/data/tipos';

export type TipoDeCampo = 'text' | 'tel' | 'email' | 'date' | 'select' | 'textarea';

export interface CampoDaFicha {
    chave: keyof FichaDoPaciente;
    rotulo: string;
    icone: string;
    tipo: TipoDeCampo;
    opcoes?: { value: string; label: string }[];
    /** Lista vem do cadastro de convênios, que muda sem tocar neste arquivo. */
    convenio?: boolean;
    /** Lista vem da equipe da clínica: aparece por nome, grava por id. */
    equipe?: boolean;
    /** Destaca em âmbar quando preenchido — alergia é aviso, não dado comum. */
    alerta?: boolean;
    somenteLeitura?: boolean;
}

export interface GrupoDaFicha {
    id: string;
    titulo: string;
    campos: CampoDaFicha[];
}

/**
 * A ficha é de leitura, com edição por campo: o lápis abre só aquela linha, em
 * vez de jogar a ficha inteira em modo de formulário. Em prontuário, editar
 * tudo de uma vez convida a alterar por engano o campo vizinho.
 */
export const GRUPOS_DA_FICHA: GrupoDaFicha[] = [
    {
        id: 'identificacao', titulo: 'Identificação', campos: [
            { chave: 'nome', rotulo: 'Nome completo', icone: 'user', tipo: 'text' },
            { chave: 'nomeSocial', rotulo: 'Nome social', icone: 'user-round', tipo: 'text' },
            { chave: 'nascimento', rotulo: 'Data de nascimento', icone: 'cake', tipo: 'date' },
            {
                chave: 'sexo', rotulo: 'Sexo', icone: 'venus-and-mars', tipo: 'select',
                opcoes: [
                    { value: 'feminino', label: 'Feminino' },
                    { value: 'masculino', label: 'Masculino' },
                    { value: 'outro', label: 'Outro' },
                    { value: 'nao-informado', label: 'Prefiro não informar' },
                ],
            },
            { chave: 'cpf', rotulo: 'CPF', icone: 'id-card', tipo: 'text' },
            { chave: 'rg', rotulo: 'RG', icone: 'id-card', tipo: 'text' },
            {
                chave: 'estadoCivil', rotulo: 'Estado civil', icone: 'heart', tipo: 'select',
                opcoes: [
                    { value: 'solteira', label: 'Solteira' },
                    { value: 'casada', label: 'Casada' },
                    { value: 'divorciada', label: 'Divorciada' },
                    { value: 'viuva', label: 'Viúva' },
                ],
            },
            { chave: 'profissao', rotulo: 'Profissão', icone: 'briefcase', tipo: 'text' },
        ],
    },
    {
        id: 'contato', titulo: 'Contato', campos: [
            { chave: 'telefone', rotulo: 'Telefone', icone: 'phone', tipo: 'tel' },
            { chave: 'telefoneSecundario', rotulo: 'Telefone secundário', icone: 'phone', tipo: 'tel' },
            { chave: 'email', rotulo: 'E-mail', icone: 'mail', tipo: 'email' },
            {
                chave: 'notificacoes', rotulo: 'Notificações', icone: 'bell', tipo: 'select',
                opcoes: [
                    { value: 'whatsapp', label: 'WhatsApp' },
                    { value: 'sms', label: 'SMS' },
                    { value: 'email', label: 'E-mail' },
                    { value: 'nenhuma', label: 'Não receber' },
                ],
            },
            { chave: 'contatoEmergencia', rotulo: 'Contato de emergência', icone: 'siren', tipo: 'text' },
        ],
    },
    {
        id: 'endereco', titulo: 'Endereço', campos: [
            { chave: 'cep', rotulo: 'CEP', icone: 'map-pin', tipo: 'text' },
            { chave: 'rua', rotulo: 'Rua', icone: 'map', tipo: 'text' },
            { chave: 'numero', rotulo: 'Número', icone: 'hash', tipo: 'text' },
            { chave: 'complemento', rotulo: 'Complemento', icone: 'building', tipo: 'text' },
            { chave: 'bairro', rotulo: 'Bairro', icone: 'map', tipo: 'text' },
            { chave: 'cidade', rotulo: 'Cidade', icone: 'building-2', tipo: 'text' },
            { chave: 'estado', rotulo: 'Estado', icone: 'map', tipo: 'text' },
        ],
    },
    {
        id: 'clinico', titulo: 'Informações clínicas', campos: [
            /* Da equipe cadastrada. Era uma lista fixa de dois nomes: quem não
               estivesse nela não podia ser responsável, e o nome que não batia com
               o cadastro gravava "sem profissional" dizendo "Campo atualizado". */
            { chave: 'profissional', rotulo: 'Profissional responsável', icone: 'stethoscope', tipo: 'select', equipe: true },
            /* Amarrado ao cadastro de convênios: o valor é o id do plano, não
               texto livre — texto livre produz "Unimed", "unimed nacional" e
               "UNIMED" no mesmo relatório. */
            { chave: 'convenioId', rotulo: 'Plano de saúde', icone: 'shield-plus', tipo: 'select', convenio: true },
            { chave: 'alergias', rotulo: 'Alergias', icone: 'triangle-alert', tipo: 'textarea', alerta: true },
            { chave: 'observacoes', rotulo: 'Observações', icone: 'notebook-pen', tipo: 'textarea' },
        ],
    },
    {
        id: 'origem', titulo: 'Cadastro e origem', campos: [
            {
                chave: 'origem', rotulo: 'Como nos conheceu', icone: 'radio', tipo: 'select',
                opcoes: [
                    { value: 'indicacao', label: 'Indicação' },
                    { value: 'instagram', label: 'Instagram' },
                    { value: 'google', label: 'Google' },
                    { value: 'passagem', label: 'Passou em frente' },
                ],
            },
            { chave: 'indicadoPor', rotulo: 'Indicado por', icone: 'users', tipo: 'text' },
            { chave: 'cadastradoEm', rotulo: 'Cadastrado em', icone: 'calendar-plus', tipo: 'date', somenteLeitura: true },
            { chave: 'atualizadoEm', rotulo: 'Última atualização', icone: 'history', tipo: 'text', somenteLeitura: true },
        ],
    },
];
